import { NgStyle } from '@angular/common';
import { Component, computed, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControlStatus,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import { FidelityAvailablePages } from 'app/shared/components/fidelity-base-page/fidelity-base-page.types';
import { cloneDeep } from 'lodash-es';
import { catchError } from 'rxjs';
import { MAX_LENGTHS } from '../../../../app.constants';
import { FidelityBasePageComponent } from '../../../../shared/components/fidelity-base-page/fidelity-base-page.component';
import { FidelityOverlayPageWrapperComponent } from '../../../../shared/components/fidelity-overlay-page-wrapper/fidelity-overlay-page-wrapper.component';
import { GenericPageLoadingStateComponent } from '../../../../shared/components/generic-page-loading-state/generic-page-loading-state.component';
import { FidelityPersonalDataService } from '../../personal-data/personal-data.service';
import { FiscalDataForUpdatePersonalDto, GetFiscalDataResponse } from '../../personal-data/personal-data.types';

@Component({
    selector: 'app-account-fiscal-info',
    imports: [
        FidelityOverlayPageWrapperComponent,
        TranslocoDirective,
        FormsModule,
        MatButton,
        MatFormField,
        MatInput,
        MatLabel,
        ReactiveFormsModule,
        NgStyle,
        MatProgressSpinner,
        GenericPageLoadingStateComponent,
        MatHint,
        MatError,
    ],
    templateUrl: './account-fiscal-info-page.component.html',
    styleUrl: './account-fiscal-info-page.component.scss',
})
export class AccountFiscalInfoPageComponent extends FidelityBasePageComponent {
    public availablePages: FidelityAvailablePages = {};
    originalData: GetFiscalDataResponse;
    fiscalForm: FormGroup;
    fiscalFormStatus: Signal<FormControlStatus | undefined>;
    protected disableCrudActions: Signal<boolean> = this.personalDataService.disableCrudActions;
    protected fetchingData: Signal<boolean> = this.personalDataService.fetchingData;

    protected disableSave = computed<boolean>(() => {
        this.fiscalFormStatus();
        return this.fiscalForm.invalid || this.disableCrudActions();
    });

    constructor(
        private fb: FormBuilder,
        private personalDataService: FidelityPersonalDataService
    ) {
        super();

        this.fiscalForm = this.fb.group({
            fiscalCode: [
                null,
                [
                    Validators.minLength(MAX_LENGTHS.FISCAL_DATA.FISCAL_CODE),
                    Validators.maxLength(MAX_LENGTHS.FISCAL_DATA.FISCAL_CODE),
                ],
            ],
            vatNumber: [
                null,
                [
                    Validators.minLength(MAX_LENGTHS.FISCAL_DATA.VAT_NUMBER),
                    Validators.maxLength(MAX_LENGTHS.FISCAL_DATA.VAT_NUMBER),
                ],
            ],
            businessName: [null, [Validators.maxLength(MAX_LENGTHS.FISCAL_DATA.BUSINESS_NAME)]],
            sdiCode: [
                null,
                [
                    Validators.minLength(MAX_LENGTHS.FISCAL_DATA.SDI_CODE),
                    Validators.maxLength(MAX_LENGTHS.FISCAL_DATA.SDI_CODE),
                ],
            ],
            pecAddress: [null, [Validators.email, Validators.maxLength(MAX_LENGTHS.FISCAL_DATA.PEC_ADDRESS)]],
        });
        this.fiscalFormStatus = toSignal(this.fiscalForm.statusChanges);

        this.personalDataService
            .getFiscalData()
            .pipe(takeUntilDestroyed(), catchError(this.handlePageError))
            .subscribe((result) => {
                this.originalData = result.result;
                this.fiscalForm.patchValue(result.result);
            });
    }

    saveChanges() {
        let dto: FiscalDataForUpdatePersonalDto = cloneDeep(this.fiscalForm.value);

        this.personalDataService
            .updateFiscalData(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((_) => {
                this.notifyResult(this.translocoService.translate('fidelity-app.account.fiscal.notification-caption'));
                this.fidelityAppNavService.back();
            });
    }

    protected readonly MAX_LENGTHS = MAX_LENGTHS;
}
